import { useCallback, useMemo } from 'react'

import { ChipList } from '@/components/ChipList/ChipList'
import {
	UnavailableItemsModal,
	WarningBanner,
} from '@/components/NewPanelProperties/components'
import { TableIndexColumn } from '@/endpoints/schemas'
import { useDeprecatedColumns } from '@/hooks'
import { FormValue } from '@/types'

import { SelectedColumn } from './SelectedColumn'
import { SelectField } from './styles'
import {
	ColumnOption,
	ColumnsPickerItem,
	ColumnsPickerProps,
	ColumnsPickerValue,
} from './types'
import { getColumnPickerData } from './utils'

const stripFieldArrayId = (column: ColumnsPickerItem): ColumnsPickerItem => {
	const { id: _id, ...rest } = column as ColumnsPickerItem & { id?: string }
	return rest
}

export const ColumnsPicker = ({
	columns,
	onChange,
	orders,
	editMode,
	testId,
}: ColumnsPickerProps) => {
	// Use the deprecated columns hook
	const {
		showDeprecatedWarning,
		isModalOpen,
		handleOpenModal,
		handleCloseModal,
		enrichmentResult,
		handleRemoveDeprecatedColumns,
		pickedColumns,
	} = useDeprecatedColumns({
		tableColumns: columns,
		onRemoveDeprecatedColumns: (cleanedColumns) => {
			onChange?.(cleanedColumns)
		},
	})

	const handleColumnChange = useCallback(
		(v: FormValue) => {
			if (typeof v === 'string') {
				const newValue: ColumnsPickerValue = [
					...pickedColumns.map(stripFieldArrayId),
					{
						...JSON.parse(v),
						...(orders && { sortType: TableIndexColumn.SortTypeEnum.ASC }),
					},
				]

				onChange && onChange(newValue)
			}
		},
		[onChange, orders, pickedColumns],
	)

	const handleUp = (item: ColumnsPickerItem) => {
		const cleanedColumns = pickedColumns.map(stripFieldArrayId)
		const newValue = [...cleanedColumns]
		const index = cleanedColumns.findIndex((v) => v.uuid === item.uuid)

		if (index > 0) {
			const b = cleanedColumns[index - 1]
			newValue[index] = b
			newValue[index - 1] = stripFieldArrayId(item)

			onChange && onChange(newValue)
		}
	}

	const handleDown = (item: ColumnsPickerItem) => {
		const cleanedColumns = pickedColumns.map(stripFieldArrayId)
		const newValue = [...cleanedColumns]
		const index = cleanedColumns.findIndex((v) => v.uuid === item.uuid)

		if (index < cleanedColumns.length - 1) {
			const b = cleanedColumns[index + 1]
			newValue[index] = b
			newValue[index + 1] = stripFieldArrayId(item)

			onChange && onChange(newValue)
		}
	}

	const handleRemove = (columnToRemove: ColumnsPickerItem) => {
		const filteredColumns = pickedColumns
			.filter((pickedColumn) => pickedColumn.uuid !== columnToRemove.uuid)
			.map(stripFieldArrayId)

		onChange?.(filteredColumns)
	}

	const onChangeItemSort = (item: ColumnsPickerItem) => {
		onChange &&
			onChange(
				pickedColumns.map((v) => {
					const cleaned = stripFieldArrayId(v)
					return v.uuid !== item.uuid
						? cleaned
						: {
								...cleaned,
								sortType:
									v.sortType === TableIndexColumn.SortTypeEnum.DESC
										? TableIndexColumn.SortTypeEnum.ASC
										: TableIndexColumn.SortTypeEnum.DESC,
							}
				}),
			)
	}

	const items = useMemo(
		() =>
			columns
				?.filter((c) => !pickedColumns.find((v) => v?.uuid === c?.uuid))
				.reduce((items, column) => {
					items.push({
						value: JSON.stringify({
							uuid: column.uuid,
							code: column.code,
						}),
						label: String(column.name),
					})
					return items
				}, [] as ColumnOption[]),
		[columns, pickedColumns],
	)

	const data = getColumnPickerData({
		tableColumns: columns,
		pickedColumns,
	})

	return (
		<>
			{/* Deprecated columns warning */}
			{editMode &&
				showDeprecatedWarning &&
				enrichmentResult.hasDeprecatedColumns && (
					<WarningBanner handleOpenModal={handleOpenModal} />
				)}

			{!editMode ? (
				<ChipList data={data} />
			) : (
				<div className="w-full border border-solid border-gray-300 rounded-md">
					<div className="border-b border-solid border-gray-300 pb-4">
						{data?.map((indexColumn, index) => {
							if (!indexColumn) return null

							return (
								<SelectedColumn
									key={index}
									indexColumn={indexColumn}
									index={index}
									valueLength={pickedColumns.length}
									onUp={handleUp}
									onDown={handleDown}
									onRemove={handleRemove}
									onChangeItemSort={onChangeItemSort}
									orders={orders}
								/>
							)
						})}
					</div>
					<SelectField
						name="column"
						options={items}
						initialValue={null}
						onChange={handleColumnChange}
						testId={testId}
					/>
				</div>
			)}
			{/* The Modal */}
			<UnavailableItemsModal
				open={isModalOpen}
				onClose={handleCloseModal}
				unavailableItems={enrichmentResult.deprecatedColumns}
				onConfirm={handleRemoveDeprecatedColumns}
			/>
		</>
	)
}
